import { gql } from "generated-graphql";

/**
 * Everything the notification preferences page renders, in one request per tab.
 *
 * The whole page — the delivery switches, the sections, the rows and their
 * per-channel choices — comes from this single query. `scope` chooses the layer
 * the values are resolved at, so the "My preferences" and "Organization
 * defaults" tabs are the same document with a different variable. Apollo caches
 * them separately by variables, which is what lets a tab keep its own answer
 * without either tab having to re-derive the other's.
 *
 * `ownEnabled` is the toggle position and is what every switch renders. `enabled`
 * and `silencedByMaster` are the server's view of what would actually be
 * delivered — they diverge from `ownEnabled` exactly while a master is muting the
 * row — and are selected as the answer to compare against rather than as the source
 * the page draws from. The page must not explain a mute from them: they describe the
 * state at load time and are stale from the first switch the reader flips, so
 * silencing is recomputed from the draft instead.
 */
export const MY_NOTIFICATION_PREFERENCE_CATALOG = gql(`
  query MyNotificationPreferenceCatalog($tenantId: ID!, $scope: NotificationPreferenceScope!) {
    myNotificationPreferenceCatalog(tenantId: $tenantId, scope: $scope) {
      channelParentSwitches {
        kind
        channel
        enabled
        source
      }
      sections {
        groupId
        label
        staffOnly
        parentSwitches {
          kind
          channel
          enabled
          source
        }
        rows {
          kind
          title
          description
          linksTo
          when {
            control
            fixedLabel
            leadTimeDays
            thresholdPercent
          }
          preferences {
            kind
            channel
            enabled
            ownEnabled
            silencedByMaster
            defaultEnabled
            source
          }
        }
      }
    }
  }
`);

/**
 * Stores a batch of preference overrides. The page sends only the entries whose
 * toggle actually moved: an untouched row must stay absent so it keeps following
 * whichever layer it was following, rather than being frozen at today's resolved
 * value.
 */
export const SET_NOTIFICATION_PREFERENCES = gql(`
  mutation SetNotificationPreferences($tenantId: ID!, $input: [SetNotificationPreferenceInput!]!) {
    setNotificationPreferences(tenantId: $tenantId, input: $input)
  }
`);

/**
 * Deletes the caller's own override rows so they follow the organization again.
 * Deliberately not a batch of writes: an absent row means "follow the
 * organization", and copying today's organization values onto the caller would
 * look identical the moment it ran and then freeze them out of every later
 * change to those defaults.
 */
export const RESET_MY_NOTIFICATION_PREFERENCES = gql(`
  mutation ResetMyNotificationPreferences($tenantId: ID!) {
    resetMyNotificationPreferences(tenantId: $tenantId)
  }
`);

/**
 * The Standard preset, which is a *delete* rather than a write.
 *
 * Encamp's recommended posture is the registry default, which is exactly what an
 * organization with no rows of its own already resolves to — so writing those
 * values would change nothing on the day it ran and would then freeze this
 * organization out of every future change Encamp makes to its recommendations.
 * Deleting achieves the same posture and keeps the organization following it.
 */
export const RESET_ORGANIZATION_NOTIFICATION_PREFERENCES = gql(`
  mutation ResetOrganizationNotificationPreferences($tenantId: ID!) {
    resetOrganizationNotificationPreferences(tenantId: $tenantId)
  }
`);
